import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Pencil, Trash2, Plus, BookOpen, Video, HelpCircle, X, Shield, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SecuritySettingsPanel from "./SecuritySettingsPanel";

type Question = {
  id: string; question_text: string; options: string[];
  correct_option_index: number; sort_order: number;
  question_text_am?: string; options_am?: string[];
};

type LessonWithQuestions = {
  id: string; title: string; sort_order: number; description: string | null;
  video_url: string | null; passing_score: number; questions: Question[];
  title_am?: string; description_am?: string;
};

type CourseWithLessons = {
  id: string; title: string; description: string | null;
  is_published: boolean; sort_order: number; lessons: LessonWithQuestions[];
  certificate_template_path: string | null;
  security_settings?: Record<string, unknown> | null;
};

type Props = {
  sessionToken: string;
  courses: CourseWithLessons[];
  coursesLoading: boolean;
  fetchCourses: () => void;
};

const CoursesTab = ({ sessionToken, courses, coursesLoading, fetchCourses }: Props) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editLesson, setEditLesson] = useState<LessonWithQuestions | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPassingScore, setEditPassingScore] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTitleAm, setEditTitleAm] = useState("");
  const [editDescAm, setEditDescAm] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingLessonId, setUploadingLessonId] = useState<string | null>(null);
  const [removingVideoId, setRemovingVideoId] = useState<string | null>(null);

  const [questionsLesson, setQuestionsLesson] = useState<LessonWithQuestions | null>(null);
  const [newQuestion, setNewQuestion] = useState({ text: "", textAm: "", options: ["", "", "", ""], optionsAm: ["", "", "", ""], correctIndex: 0 });
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editQText, setEditQText] = useState("");
  const [editQTextAm, setEditQTextAm] = useState("");
  const [editQOptions, setEditQOptions] = useState(["", "", "", ""]);
  const [editQOptionsAm, setEditQOptionsAm] = useState(["", "", "", ""]);
  const [editQCorrect, setEditQCorrect] = useState(0);
  const [savingQ, setSavingQ] = useState(false);

  const [showAddCourse, setShowAddCourse] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState("");
  const [newCourseDesc, setNewCourseDesc] = useState("");
  const [addingCourse, setAddingCourse] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("");

  const [addLessonCourseId, setAddLessonCourseId] = useState<string | null>(null);
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [newLessonDesc, setNewLessonDesc] = useState("");
  const [addingLesson, setAddingLesson] = useState(false);

  const [editCourse, setEditCourse] = useState<CourseWithLessons | null>(null);
  const [editCourseTitle, setEditCourseTitle] = useState("");
  const [editCourseDesc, setEditCourseDesc] = useState("");
  const [savingCourse, setSavingCourse] = useState(false);

  const handleEditLesson = (lesson: LessonWithQuestions) => {
    setEditLesson(lesson);
    setEditTitle(lesson.title);
    setEditPassingScore(String(lesson.passing_score));
    setEditDescription(lesson.description || "");
    setEditTitleAm(lesson.title_am || "");
    setEditDescAm(lesson.description_am || "");
  };

  const handleSaveLesson = async () => {
    if (!editLesson) return;
    setSaving(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_lesson", sessionToken, lessonId: editLesson.id, title: editTitle, passing_score: editPassingScore, description: editDescription, title_am: editTitleAm, description_am: editDescAm },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Lesson updated" });
      setEditLesson(null);
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleUploadVideo = async (lessonId: string, file: File) => {
    setUploadingLessonId(lessonId);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "get_upload_url", sessionToken, lessonId, fileName: file.name },
      });
      if (res.error || !res.data?.path) throw new Error(res.data?.error || "Failed to get upload URL");
      const { error: uploadError } = await supabase.storage.from("course-videos").uploadToSignedUrl(res.data.path, res.data.token, file);
      if (uploadError) throw uploadError;
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_lesson", sessionToken, lessonId, video_url: res.data.path },
      });
      toast({ title: "Video uploaded successfully!" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally { setUploadingLessonId(null); }
  };

  const handleRemoveVideo = async (lessonId: string) => {
    setRemovingVideoId(lessonId);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "remove_video", sessionToken, lessonId },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Video removed" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setRemovingVideoId(null); }
  };

  const handleAddCourse = async () => {
    if (!newCourseTitle.trim()) return;
    setAddingCourse(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "add_course", sessionToken, title: newCourseTitle, description: newCourseDesc },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Course created!" });
      setShowAddCourse(false); setNewCourseTitle(""); setNewCourseDesc("");
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setAddingCourse(false); }
  };

  const handleUpdateCourse = async () => {
    if (!editCourse || !editCourseTitle.trim()) return;
    setSavingCourse(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_course", sessionToken, courseId: editCourse.id, title: editCourseTitle, description: editCourseDesc },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Course updated!" });
      setEditCourse(null);
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSavingCourse(false); }
  };

  const handleDeleteCourse = async (courseId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_course", sessionToken, courseId },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Course deleted" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleTogglePublish = async (courseId: string, publish: boolean) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "toggle_publish", sessionToken, courseId, is_published: publish },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: publish ? "Course published!" : "Course unpublished" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAddLesson = async () => {
    if (!addLessonCourseId || !newLessonTitle.trim()) return;
    setAddingLesson(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "add_lesson", sessionToken, courseId: addLessonCourseId, title: newLessonTitle, description: newLessonDesc },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Lesson added!" });
      setAddLessonCourseId(null); setNewLessonTitle(""); setNewLessonDesc("");
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setAddingLesson(false); }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_lesson", sessionToken, lessonId },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Lesson deleted" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAddQuestion = async () => {
    if (!questionsLesson || !newQuestion.text.trim()) return;
    setSavingQ(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "add_question", sessionToken, lessonId: questionsLesson.id,
          questionText: newQuestion.text, options: newQuestion.options.filter(o => o.trim()),
          correctOptionIndex: newQuestion.correctIndex,
          question_text_am: newQuestion.textAm || undefined,
          options_am: newQuestion.optionsAm.filter(o => o.trim()).length > 0 ? newQuestion.optionsAm.filter(o => o.trim()) : undefined,
        },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Question added" });
      setNewQuestion({ text: "", textAm: "", options: ["", "", "", ""], optionsAm: ["", "", "", ""], correctIndex: 0 });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSavingQ(false); }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;
    setSavingQ(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "update_question", sessionToken, questionId: editingQuestion.id,
          question_text: editQText, options: editQOptions.filter(o => o.trim()),
          correct_option_index: editQCorrect,
          question_text_am: editQTextAm || undefined,
          options_am: editQOptionsAm.filter(o => o.trim()).length > 0 ? editQOptionsAm.filter(o => o.trim()) : undefined,
        },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Question updated" });
      setEditingQuestion(null);
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSavingQ(false); }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_question", sessionToken, questionId },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Question deleted" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const currentQuestionsLesson = questionsLesson
    ? courses.flatMap(c => c.lessons).find(l => l.id === questionsLesson.id) || questionsLesson
    : null;

  const renderCourseContent = (course: CourseWithLessons) => (
    <div key={course.id} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{course.title}</h3>
          {course.description && <p className="text-sm text-muted-foreground">{course.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={course.is_published}
              onCheckedChange={() => handleTogglePublish(course.id, !course.is_published)}
            />
            <Badge variant={course.is_published ? "default" : "secondary"}>
              {course.is_published ? "Published" : "Draft"}
            </Badge>
          </div>
          <Button size="sm" variant="outline" onClick={() => { 
            setEditCourse(course); 
            setEditCourseTitle(course.title); 
            setEditCourseDesc(course.description || ""); 
          }}>
            <Pencil className="w-3 h-3 mr-1" /> Edit Course
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setAddLessonCourseId(course.id); setNewLessonTitle(""); setNewLessonDesc(""); }}>
            <Plus className="w-3 h-3 mr-1" /> Add Lesson
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete course "{course.title}"?</AlertDialogTitle>
                <AlertDialogDescription>This will permanently delete the course, all its lessons, questions, and related data.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleDeleteCourse(course.id)}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Collapsible>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 hover:bg-muted transition-colors text-sm">
            <span className="flex items-center gap-2 font-medium">
              <Shield className="w-4 h-4 text-primary" /> Security Settings
            </span>
            <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <SecuritySettingsPanel
            sessionToken={sessionToken}
            targetType="course"
            targetId={course.id}
            initialSettings={course.security_settings as never}
            onSaved={() => fetchCourses()}
          />
        </CollapsibleContent>
      </Collapsible>

      <input ref={fileInputRef} type="file" accept="video/*" className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const lessonId = fileInputRef.current?.getAttribute("data-lesson-id");
          if (file && lessonId) handleUploadVideo(lessonId, file);
          e.target.value = "";
        }}
      />

      {course.lessons.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No lessons yet. Add one above.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Lesson</TableHead>
                  <TableHead>Video</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead>Pass %</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {course.lessons.map((lesson, i) => (
                  <TableRow key={lesson.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{lesson.title}</TableCell>
                    <TableCell>
                      {uploadingLessonId === lesson.id ? (
                        <Badge variant="secondary"><Loader2 className="w-3 h-3 animate-spin mr-1" />Uploading...</Badge>
                      ) : removingVideoId === lesson.id ? (
                        <Badge variant="secondary"><Loader2 className="w-3 h-3 animate-spin mr-1" />Removing...</Badge>
                      ) : (
                        <Badge variant={lesson.video_url ? "default" : "secondary"}>
                          {lesson.video_url ? "Uploaded" : "No video"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{lesson.questions.length} questions</TableCell>
                    <TableCell>{lesson.passing_score}%</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleEditLesson(lesson)} title="Edit"><Pencil className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" title="Upload video"
                          onClick={() => { fileInputRef.current?.setAttribute("data-lesson-id", lesson.id); fileInputRef.current?.click(); }}
                          disabled={uploadingLessonId === lesson.id}><Video className="w-3 h-3" /></Button>
                        {lesson.video_url && (
                          <Button size="sm" variant="outline" title="Remove video"
                            onClick={() => handleRemoveVideo(lesson.id)} disabled={removingVideoId === lesson.id}>
                            <X className="w-3 h-3 text-destructive" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setQuestionsLesson(lesson)} title="Questions"><HelpCircle className="w-3 h-3" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" title="Delete lesson"><Trash2 className="w-3 h-3 text-destructive" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete lesson "{lesson.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete the lesson and all its questions.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteLesson(lesson.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Course Management</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddCourse(true)}><Plus className="w-4 h-4 mr-1" /> Add Course</Button>
          <Button variant="outline" onClick={fetchCourses} disabled={coursesLoading}>
            {coursesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      {coursesLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : courses.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <BookOpen className="w-10 h-10 mx-auto mb-3" /><p>No courses yet. Create one to get started.</p>
        </CardContent></Card>
      ) : courses.length === 1 ? (
        renderCourseContent(courses[0])
      ) : (
        <Tabs value={activeTab || courses[0]?.id} onValueChange={setActiveTab}>
          <TabsList>
            {courses.map((c) => (<TabsTrigger key={c.id} value={c.id}>{c.title}</TabsTrigger>))}
          </TabsList>
          {courses.map((c) => (<TabsContent key={c.id} value={c.id}>{renderCourseContent(c)}</TabsContent>))}
        </Tabs>
      )}

      {/* Add Course Dialog */}
      <Dialog open={showAddCourse} onOpenChange={setShowAddCourse}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Course Title</Label><Input value={newCourseTitle} onChange={(e) => setNewCourseTitle(e.target.value)} placeholder="e.g. Advanced Forex Trading" /></div>
            <div className="space-y-2"><Label>Description (optional)</Label><Input value={newCourseDesc} onChange={(e) => setNewCourseDesc(e.target.value)} placeholder="Brief course description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCourse(false)}>Cancel</Button>
            <Button onClick={handleAddCourse} disabled={addingCourse || !newCourseTitle.trim()}>
              {addingCourse && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={!!editCourse} onOpenChange={(open) => !open && setEditCourse(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Course Title</Label><Input value={editCourseTitle} onChange={(e) => setEditCourseTitle(e.target.value)} /></div>
            <div className="space-y-2"><Label>Description (optional)</Label><Input value={editCourseDesc} onChange={(e) => setEditCourseDesc(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCourse(null)}>Cancel</Button>
            <Button onClick={handleUpdateCourse} disabled={savingCourse || !editCourseTitle.trim()}>
              {savingCourse && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!addLessonCourseId} onOpenChange={(open) => !open && setAddLessonCourseId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Lesson Title</Label><Input value={newLessonTitle} onChange={(e) => setNewLessonTitle(e.target.value)} placeholder="e.g. Introduction to Charts" /></div>
            <div className="space-y-2"><Label>Description (optional)</Label><Input value={newLessonDesc} onChange={(e) => setNewLessonDesc(e.target.value)} placeholder="Brief lesson description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLessonCourseId(null)}>Cancel</Button>
            <Button onClick={handleAddLesson} disabled={addingLesson || !newLessonTitle.trim()}>
              {addingLesson && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add Lesson
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lesson Dialog */}
      <Dialog open={!!editLesson} onOpenChange={(open) => !open && setEditLesson(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title (English)</Label><Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} /></div>
            <div className="space-y-2"><Label>Title (አማርኛ)</Label><Input value={editTitleAm} onChange={(e) => setEditTitleAm(e.target.value)} placeholder="የትምህርት ርዕስ" /></div>
            <div className="space-y-2"><Label>Description (English)</Label><Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} /></div>
            <div className="space-y-2"><Label>Description (አማርኛ)</Label><Input value={editDescAm} onChange={(e) => setEditDescAm(e.target.value)} placeholder="የትምህርት ማብራሪያ" /></div>
            <div className="space-y-2"><Label>Passing Score (%)</Label><Input type="number" min="0" max="100" value={editPassingScore} onChange={(e) => setEditPassingScore(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLesson(null)}>Cancel</Button>
            <Button onClick={handleSaveLesson} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Questions Dialog */}
      <Dialog open={!!questionsLesson} onOpenChange={(open) => !open && setQuestionsLesson(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Questions: {questionsLesson?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(currentQuestionsLesson?.questions || []).map((q, i) => (
              <Card key={q.id} className="p-3">
                {editingQuestion?.id === q.id ? (
                  <div className="space-y-3">
                    <Input value={editQText} onChange={(e) => setEditQText(e.target.value)} placeholder="Question (English)" />
                    <Input value={editQTextAm} onChange={(e) => setEditQTextAm(e.target.value)} placeholder="Question (አማርኛ)" />
                    <p className="text-xs text-muted-foreground font-semibold">Options (English):</p>
                    {editQOptions.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="radio" name="edit-correct" checked={editQCorrect === oi} onChange={() => setEditQCorrect(oi)} />
                        <Input value={opt} onChange={(e) => { const n = [...editQOptions]; n[oi] = e.target.value; setEditQOptions(n); }} placeholder={`Option ${oi + 1}`} />
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground font-semibold">Options (አማርኛ):</p>
                    {editQOptionsAm.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Input value={opt} onChange={(e) => { const n = [...editQOptionsAm]; n[oi] = e.target.value; setEditQOptionsAm(n); }} placeholder={`አማርኛ Option ${oi + 1}`} />
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleUpdateQuestion} disabled={savingQ}>{savingQ && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingQuestion(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{i + 1}. {q.question_text}</p>
                      {q.question_text_am && <p className="text-xs text-muted-foreground">አማ: {q.question_text_am}</p>}
                      <div className="mt-1 space-y-0.5">
                        {(Array.isArray(q.options) ? q.options : []).map((opt: string, oi: number) => (
                          <p key={oi} className={`text-xs ${oi === q.correct_option_index ? "text-green-600 font-semibold" : "text-muted-foreground"}`}>
                            {String.fromCharCode(65 + oi)}. {opt} {oi === q.correct_option_index && "✓"}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => {
                        setEditingQuestion(q); setEditQText(q.question_text); setEditQTextAm(q.question_text_am || "");
                        const opts = Array.isArray(q.options) ? q.options : [];
                        setEditQOptions([opts[0] || "", opts[1] || "", opts[2] || "", opts[3] || ""]);
                        const optsAm = Array.isArray(q.options_am) ? q.options_am : [];
                        setEditQOptionsAm([optsAm[0] || "", optsAm[1] || "", optsAm[2] || "", optsAm[3] || ""]);
                        setEditQCorrect(q.correct_option_index);
                      }}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteQuestion(q.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}

            <Card className="p-3 border-dashed">
              <p className="text-sm font-medium mb-2">Add New Question</p>
              <div className="space-y-3">
                <Input value={newQuestion.text} onChange={(e) => setNewQuestion(p => ({ ...p, text: e.target.value }))} placeholder="Question (English)" />
                <Input value={newQuestion.textAm} onChange={(e) => setNewQuestion(p => ({ ...p, textAm: e.target.value }))} placeholder="Question (አማርኛ)" />
                <p className="text-xs text-muted-foreground font-semibold">Options (English):</p>
                {newQuestion.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" name="new-correct" checked={newQuestion.correctIndex === oi} onChange={() => setNewQuestion(p => ({ ...p, correctIndex: oi }))} />
                    <Input value={opt} onChange={(e) => { const n = [...newQuestion.options]; n[oi] = e.target.value; setNewQuestion(p => ({ ...p, options: n })); }} placeholder={`Option ${oi + 1}`} />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground font-semibold">Options (አማርኛ):</p>
                {newQuestion.optionsAm.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <Input value={opt} onChange={(e) => { const n = [...newQuestion.optionsAm]; n[oi] = e.target.value; setNewQuestion(p => ({ ...p, optionsAm: n })); }} placeholder={`አማርኛ Option ${oi + 1}`} />
                  </div>
                ))}
                <Button size="sm" onClick={handleAddQuestion} disabled={savingQ || !newQuestion.text.trim()}>
                  {savingQ && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}<Plus className="w-3 h-3 mr-1" /> Add Question
                </Button>
              </div>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursesTab;
